import { zodResolver } from '@hookform/resolvers/zod';
import { type LoginDto, LoginSchema } from '@nestjs-boilerplate/types';
import { Link } from '@tanstack/react-router';
import { Eye, EyeOff } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';

import logo from '@/assets/OKYC logo.webp';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';

import { useLogin } from '../hooks/use-login';

interface LoginFormProps {
  redirectTo?: string;
}

export function LoginForm({ redirectTo }: LoginFormProps) {
  const { mutate: login, isPending } = useLogin(redirectTo);
  const [showPassword, setShowPassword] = useState(false);

  const form = useForm<LoginDto>({
    resolver: zodResolver(LoginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  function onSubmit(data: LoginDto) {
    login(data);
  }

  return (
    <Card className="w-full max-w-md rounded-[5px] border border-[#D4DAE0]">
      <CardHeader className="space-y-4 text-center">
        <img src={logo} alt="OKYC Logo" className="mx-auto h-16 w-auto" />

        <div className="space-y-2">
          <h1 className="text-[20px] font-semibold leading-[120%] text-[#051923]">Login</h1>
          <p className="text-[16px] font-normal leading-[22px] text-[#051923]">
            Welcome to Optimal KYC.
          </p>
        </div>

        <p className="text-[16px] font-normal leading-[22px] text-[#051923]">
          Take Anti Money Laundering compliance to the next level with AI PEP & Sanctions Screening
          and automated Adverse Media Investigation.
        </p>
      </CardHeader>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <CardContent className="space-y-4">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email Address</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      placeholder="john.smith@company.com"
                      autoComplete="email"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Password</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        placeholder="************"
                        autoComplete="current-password"
                        className="pr-10"
                        {...field}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        {showPassword ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-end">
              <Link
                to="/forgot-password"
                className="text-[16px] font-semibold leading-[140%] text-[#131313] underline"
              >
                Forgot Password?
              </Link>
            </div>
          </CardContent>

          <CardFooter className="flex flex-col space-y-4">
            <Button
              type="submit"
              className="h-[45px] w-full rounded-[5px] bg-[#131313] text-[18px] font-medium leading-[100%] text-white transition-opacity duration-300 ease-out hover:bg-[#131313]/90"
              disabled={isPending}
            >
              {isPending ? 'Logging in...' : 'Login'}
            </Button>

            <p className="text-center text-[16px] font-normal leading-[22px] text-[#051923]">
              Don&apos;t have an account?{' '}
              <Link
                to="/register"
                className="font-semibold leading-[140%] text-[#131313] underline"
              >
                Sign up now
              </Link>
            </p>
          </CardFooter>
        </form>
      </Form>
    </Card>
  );
}
